import { BackIcon, ForwardIcon } from '@assets/icons';
import { Button } from '@components/button';
import {
  useGetWorkspaceResourcesAvailableChannelsQuery,
  useGetWorkspaceResourcesWorkspaceChannelsQuery,
} from '@generated/graphql';
import { Grid, Stack } from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ResourcesType, TransferList, intersection, not } from './TransferList';

type ChannelsTransferListProps = {
  organizationId: string;
  workspaceId: string;
  selectedChannels: ResourcesType;
  setSelectedChannels: Dispatch<SetStateAction<ResourcesType>>;
  setChannelsDirty: Dispatch<SetStateAction<boolean>>;
};

const getChannels = (
  channels: { channelId: string; name: string }[],
  selected: string[],
  available?: boolean,
) => {
  return (channels ?? [])
    .filter(({ channelId }) =>
      available ? !selected.includes(channelId) : selected.includes(channelId),
    )
    .map(({ channelId, name }) => ({ id: channelId, name }));
};

export const ChannelsTransferList = ({
  organizationId,
  workspaceId,
  selectedChannels,
  setSelectedChannels,
  setChannelsDirty,
}: ChannelsTransferListProps) => {
  const [checked, setChecked] = useState<ResourcesType>([]);
  const [right, setRight] = useState<ResourcesType>([]);
  const { data: availableChannels, loading: availableChannelsLoading } =
    useGetWorkspaceResourcesAvailableChannelsQuery({
      variables: {
        organizationId,
      },
    });

  const { data: includedChannels, loading: includedChannelsLoading } =
    useGetWorkspaceResourcesWorkspaceChannelsQuery({
      variables: {
        workspaceId,
      },
    });
  const selectedChannelsIds = (includedChannels?.getChannels ?? []).map((c) => c.channelId);

  useEffect(() => {
    if (!availableChannelsLoading && !includedChannelsLoading) {
      setRight(getChannels(availableChannels?.getChannels, selectedChannelsIds, true));
      setSelectedChannels(getChannels(availableChannels?.getChannels, selectedChannelsIds, false));
    }

    return () => {
      setRight([]);
      setSelectedChannels([]);
    };
    // Init-once when both loadings flip false; deliberately omits data deps
    // to avoid overwriting user edits on background refetches. WorkspaceResourcesModal
    // is slated for rework — revisit this pattern then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableChannelsLoading, includedChannelsLoading]);

  const leftChecked = intersection(checked, selectedChannels);
  const rightChecked = intersection(checked, right);
  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setSelectedChannels(not(selectedChannels, leftChecked));
    setChannelsDirty(true);
    setChecked(not(checked, leftChecked));
  };
  const handleCheckedLeft = () => {
    setSelectedChannels(selectedChannels.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChannelsDirty(true);
    setChecked(not(checked, rightChecked));
  };

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item xs={5}>
        <TransferList
          title="Included"
          items={selectedChannels}
          checked={checked}
          setChecked={setChecked}
        />
      </Grid>
      <Grid item xs={2}>
        <Grid container direction="column" alignItems="center">
          <Stack gap={4}>
            <Button
              fullWidth
              variant="secondary"
              onClick={handleCheckedLeft}
              data-cy="Channel-Transfer-List-Add-Button"
              startIcon={BackIcon}
              disabled={rightChecked.length === 0}
            >
              Add
            </Button>
            <Button
              fullWidth
              variant="secondary"
              data-cy="Channel-Transfer-List-Remove-Button"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              endIcon={ForwardIcon}
            >
              Remove
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Grid item xs={5}>
        <TransferList title="Excluded" items={right} checked={checked} setChecked={setChecked} />
      </Grid>
    </Grid>
  );
};
